/**
 * Layer① 低レベル fetch ラッパー
 *
 * 責務:
 * - fetch() の呼び出し
 * - Cookie の送信（credentials: 'include'）。API は別オリジン（app → api）のため、
 *   指定しないとテストアクセスの Cookie（#3736）が送られない（#3739）
 * - Accept / Content-Type ヘッダーのデフォルト付与
 * - 追加ヘッダー（キャンペーンヘッダー・Authorization）の受け取りと fetch への引き渡し
 * - リクエストボディの JSON.stringify
 * - 204 / 空ボディの検出
 * - エラーレスポンス body の code フィールド抽出と ApiError(status, code) の throw
 * - 401 発生時に useTokenStore.notify401() を呼んで tokenRefresh をトリガーする
 * - 403 REAUTH_REQUIRED 発生時に useTokenStore.notifyReauthRequired() を呼んで再認証画面への移動をトリガーする
 *
 * 非担当（上位層に委ねる）:
 * - base URL の結合（Layer② のドメイン関数）
 * - Zod バリデーション（Layer②）
 * - ログ出力（Layer②）
 * - AbortSignal / タイムアウト（将来 RequestOptions 拡張で対応予定）
 * - キャッシュ制御（Layer④ TanStack Query 側）
 */

import { useTokenStore } from '@/store/useTokenStore'

/** 構造化 HTTP エラー型。status で数値判定できる */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code?: string,
    public readonly body?: unknown,
  ) {
    super(`HTTP ${status}`)
    this.name = 'ApiError'
  }
}

/** バックエンドが再認証（本人確認）を求めるときのエラーコード（#3610）。403 で返る。401 ではない */
export const REAUTH_REQUIRED_CODE = 'REAUTH_REQUIRED'

/** 403 REAUTH_REQUIRED かどうか。画面側で「エラー文言を出さない」等の分岐に使う */
export const isReauthRequiredError = (e: unknown): e is ApiError =>
  e instanceof ApiError && e.status === 403 && e.code === REAUTH_REQUIRED_CODE

/**
 * バックエンドで同時の書き込みが衝突（デッドロック）し、DB がトランザクションを取り消したときのエラーコード（#3851）。409 で返る。
 * 取り消されたトランザクションの書き込みは残らない。ただし途中で一度確定してから次のトランザクションでぶつかる処理
 * （まとめて獲得のスタンプなど）は、確定した分が残る
 */
export const RESOURCE_BUSY_CODE = 'RESOURCE_BUSY'

/**
 * 409 RESOURCE_BUSY かどうか。もう一度操作すれば通るので、
 * 409 を「回答済み」「登録済み」などに読み替える画面は、この判定を先に行うこと（#3871）
 */
export const isResourceBusyError = (e: unknown): e is ApiError =>
  e instanceof ApiError && e.status === 409 && e.code === RESOURCE_BUSY_CODE

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  headers?: Record<string, string>
}

/**
 * 型安全な低レベル fetch ラッパー。
 * 戻り値は T | undefined。204 / 空ボディの場合は undefined を返す。
 * 呼び出し側は T | undefined として扱うこと。
 * void 系 API（POST stamps 等）は Layer② のドメイン関数で Promise<void> に変換する。
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T | undefined> {
  const { method = 'GET', body, headers } = options

  const res = await fetch(path, {
    method,
    // API は別オリジンのため、テストアクセスの Cookie（HttpOnly・Domain=.raund.net）を送るには include が必要（#3739）。
    // API の CORS は allow_credentials: true 設定済み
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    // エラーレスポンスの body を parse して code を取得（XState の onError 向け）
    // API レスポンスのフォーマットは { code: "..." } と { error: { code: "..." } } の両方に対応する
    const errorBody = (await res.json().catch(() => ({}))) as { code?: string; error?: { code?: string } }
    const code = errorBody.code ?? errorBody.error?.code
    const error = new ApiError(res.status, code, errorBody)
    // 401 は認証トークン期限切れの可能性があるため、XState tokenRefresh をトリガーする。
    // idToken がある（認証済み）場合のみ通知し、ゲスト API 由来の 401 は無視する。
    if (res.status === 401 && useTokenStore.getState().idToken !== null) {
      useTokenStore.getState().notify401()
    }
    // 403 LINE_NOT_FRIEND は LINE友達未登録を示す専用コード → LINE友達追加リダイレクトをトリガーする。
    // idToken がある（認証済み）場合のみ通知し、他の 403 には影響しない。
    if (res.status === 403 && code === 'LINE_NOT_FRIEND' && useTokenStore.getState().idToken !== null) {
      useTokenStore.getState().notifyLineFriendRequired()
    }
    // 403 REAUTH_REQUIRED はログイン（再認証）から一定時間を過ぎたことを示す専用コード（#3610）。
    // 画面ごとではなく共通で受け、auth の再認証画面へ移動させる（_app.tsx）。
    // 401 ではないのでログアウト・トークンリフレッシュの経路には入れない。
    if (res.status === 403 && code === REAUTH_REQUIRED_CODE && useTokenStore.getState().idToken !== null) {
      useTokenStore.getState().notifyReauthRequired()
    }
    throw error
  }

  // 204 はボディなし確定
  if (res.status === 204) {
    return undefined
  }

  // content-length ヘッダーなしの空ボディ（200/201 等）も空文字で検出
  // ⚠️ 呼び出し側は戻り値を T | undefined として扱うこと
  const responseText = await res.text()
  if (responseText === '') {
    return undefined
  }

  return JSON.parse(responseText) as T
}

/** 薄いメソッドラッパー（内部利用） */
export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { method: 'GET', ...options }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { method: 'POST', body, ...options }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { method: 'PUT', body, ...options }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { method: 'DELETE', ...options }),
}
